/**
 * request-a-quote.js
 * assets/request-a-quote.js
 *
 * Load in theme.liquid (before </body>):
 *   <script src="{{ 'dealers-zip-lookup.js' | asset_url }}" defer></script>
 *   <script src="{{ 'request-a-quote.js' | asset_url }}" defer></script>
 *
 * Integration: Form → Zapier Webhook → SharpSpring
 */

(function () {
  'use strict';

  // ─── Config ──────────────────────────────────────────────────────────────────
  const CONFIG = {
    // Paste your Zapier "Catch Hook" URL here
    zapierWebhookUrl: 'https://hooks.zapier.com/hooks/catch/24999803/uv8ufef/',
  };

  // ─── DOM References ───────────────────────────────────────────────────────────
  const $ = (id) => document.getElementById(id);

  const overlay          = $('raq-overlay');
  const closeBtn          = $('raq-close');
  const trigger           = $('raq-trigger');
  const form              = $('raq-form');
  const findBtn           = $('raq-btn-find');
  const submitBtn         = $('raq-btn-submit');
  const shadingDirectBtn  = $('raq-btn-shading-direct'); // NEW
  const successEl         = $('raq-success');
  const dealerCard        = $('raq-dealer-card');
  const dealerNone        = $('raq-dealer-none');

  if (!overlay || !trigger) return;

  // ─── State ────────────────────────────────────────────────────────────────────
  let quoteData = {
    product:          {},
    variant:          {},
    personalizations: [],
    dealer:           null,
  };

  // ─── 1. Read Shopify Variant Data ─────────────────────────────────────────────
  function readProductData() {
    const btn = $('raq-trigger');
    if (!btn) return;

    quoteData.product = {
      id:    btn.dataset.productId,
      title: btn.dataset.productTitle,
      image: btn.dataset.productImage,
    };

    const variantInput = document.querySelector('[name="id"]');
    const variantId    = variantInput ? variantInput.value : btn.dataset.variantId;

    const productJsonEl =
      document.getElementById('ProductJson-product-template') ||
      document.querySelector('[data-product-json]') ||
      document.querySelector('script[type="application/json"][data-product-json]');

    if (productJsonEl) {
      try {
        const productJson = JSON.parse(productJsonEl.textContent);
        const variant = productJson.variants.find((v) => String(v.id) === String(variantId));
        if (variant) {
          quoteData.variant = {
            id:      variant.id,
            title:   variant.title,
            sku:     variant.sku,
            price:   (variant.price / 100).toFixed(2),
            options: productJson.options.map((opt, i) => ({
              name:  opt.name,
              value: variant.options[i],
            })),
          };
        }
      } catch (e) {
        console.warn('[RAQ] Could not parse product JSON:', e);
      }
    }
  }

  // ─── 2. Read Zepto Product Personalizer Selections ───────────────────────────
  //
  // Zepto injects hidden inputs named properties[Label] into the product form.
  // We read those directly — no Zepto JS API needed.
  //
  // Each personalization becomes its own flat key in the Zapier payload so
  // SharpSpring can map them to individual custom fields:
  //   e.g. { "Color": "Bronze", "Fabric": "Sunscreen 5%", "Width": "120\"" }

  function readZeptoPersonalizations() {
    const personalizations = [];

    // Read Zepto hidden inputs from the product form
    document.querySelectorAll(
      'form[action="/cart/add"] input[name^="properties["], ' +
      'form[action="/cart/add"] select[name^="properties["], ' +
      'form[action="/cart/add"] textarea[name^="properties["]'
    ).forEach((input) => {
      const label = input.name.replace(/^properties\[(.+)\]$/, '$1');
      const value = input.value?.trim();
      if (value) personalizations.push({ label, value });
    });

    // Zepto JS API fallback (if available)
    if (window.ZeptoProductPersonalizer?.getValues) {
      try {
        Object.entries(window.ZeptoProductPersonalizer.getValues()).forEach(([label, value]) => {
          if (value && !personalizations.find((p) => p.label === label)) {
            personalizations.push({ label, value: String(value) });
          }
        });
      } catch {}
    }

    quoteData.personalizations = personalizations;
  }

  // ─── 3. Build the Zapier Payload ──────────────────────────────────────────────
  //
  // Everything is flat so Zapier can map each field directly to a SharpSpring
  // field with no transformation step needed.
  //
  // Zepto personalizations are spread as individual keys:
  //   personalizer_Color, personalizer_Fabric, personalizer_Width, etc.
  //
  // Variant options are spread too:
  //   variant_Size, variant_Material, etc.
  //
  // A single "configuration_summary" string is also included for use in the
  // SharpSpring email template as a simple merge field.

  function buildZapierPayload() {
    // Collect contact-method checkboxes into a comma-separated string
    const contactMethods = ['phone', 'email', 'text']
      .filter((m) => document.getElementById(`raq-contact-${m}`)?.checked)
      .join(', ');

    const customer = {
      first_name: $('raq-first-name').value.trim(),
      last_name:  $('raq-last-name').value.trim(),
      email:      $('raq-email').value.trim(),
      phone:      $('raq-phone').value.trim(),
      zip:        $('raq-zip').value.trim(),
      message:    $('raq-message').value.trim(),
    };

    // Flat variant options: variant_Size, variant_Material …
    const variantFields = {};
    (quoteData.variant.options || []).forEach(({ name, value }) => {
      variantFields[`variant_${name}`] = value;
    });

    // Flat personalizer options: personalizer_Color, personalizer_Fabric …
    const personalizerFields = {};
    quoteData.personalizations.forEach(({ label, value }) => {
      personalizerFields[`personalizer_${label}`] = value;
    });

    // Human-readable summary for the email merge field
    const variantLines = (quoteData.variant.options || [])
      .map((o) => `${o.name}: ${o.value}`);
    const personalizerLines = quoteData.personalizations
      .map((p) => `${p.label}: ${p.value}`);
    const configurationSummary = [...variantLines, ...personalizerLines].join('\n');

    return {
      // ── Contact ──────────────────────────────────────────────
      first_name:   customer.first_name,
      last_name:    customer.last_name,
      email:        customer.email,
      phone:        customer.phone,
      zip:          customer.zip,
      message:      customer.message,

      // ── Preferences ───────────────────────────────────────────
      contact_methods:       contactMethods,
      in_home_consultation:  $('raq-consultation')?.checked ? 'Yes' : 'No',
      subscribe_promotions:  $('raq-subscribe')?.checked    ? 'Yes' : 'No',
      interest:              $('raq-interest')?.value       || '',
      lead_type:                   'ERAQ',                             

      // ── Product ───────────────────────────────────────────────
      product_title:  quoteData.product.title  || '',
      product_id:     quoteData.product.id     || '',
      variant_title:  quoteData.variant.title  || '',
      variant_sku:    quoteData.variant.sku    || '',
      variant_price:  quoteData.variant.price  || '',

      // ── Variant options (flat) ────────────────────────────────
      ...variantFields,

      // ── Zepto personalizer (flat) ─────────────────────────────
      ...personalizerFields,

      // ── Full summary (for SharpSpring email merge field) ──────
      configuration_summary: configurationSummary,

      // ── Dealer (SharpSpring dealer_id links to SS campaign) ───
      dealer_name:  quoteData.dealer?.name      || '',
      dealer_id:    quoteData.dealer?.dealer_id || '',  // SharpSpring campaign ID
      dealer_email: quoteData.dealer?.email     || '',
      dealer_zip:   quoteData.dealer?.zip       || '',

      // ── Meta ──────────────────────────────────────────────────
      submitted_at: new Date().toISOString(),
      page_url:     window.location.href,
    };
  }

  // ─── 4. Populate the Modal Product Summary ────────────────────────────────────
  function populateProductSummary() {
    const img = $('raq-product-image');
    if (img && quoteData.product.image) {
      img.src = quoteData.product.image;
      img.alt = quoteData.product.title;
    }

    const nameEl = $('raq-product-name');
    if (nameEl) nameEl.textContent = quoteData.product.title || '—';

    const pillsEl = $('raq-variant-pills');
    if (pillsEl) {
      pillsEl.innerHTML = '';
      (quoteData.variant.options || []).forEach(({ name, value }) => {
        const pill = document.createElement('span');
        pill.className = 'raq-pill';
        pill.innerHTML = `<strong>${escapeHtml(name)}:</strong> ${escapeHtml(value)}`;
        pillsEl.appendChild(pill);
      });
    }

    const personEl = $('raq-personalizations');
    if (personEl) {
      personEl.innerHTML = '';
      quoteData.personalizations.forEach(({ label, value }) => {
        const row = document.createElement('div');
        row.className = 'raq-personalization-row';
        row.innerHTML = `<span class="raq-personalization-label">${escapeHtml(label)}</span>` +
                        `<span class="raq-personalization-value">${escapeHtml(value)}</span>`;
        personEl.appendChild(row);
      });
    }
  }

  // ─── 5. Open / Close Modal ────────────────────────────────────────────────────
  function openModal() {
    readProductData();
    readZeptoPersonalizations();
    populateProductSummary();

    overlay.removeAttribute('aria-hidden');
    overlay.classList.add('raq-overlay--active');
    document.body.style.overflow = 'hidden';

    form.style.display = '';
    successEl.hidden = true;
    form.reset();
    dealerCard.hidden = true;
    dealerNone.hidden = true;
    submitBtn.hidden = false;         // NEW — reset to default state
    shadingDirectBtn.hidden = true;   // NEW — reset to default state
    quoteData.dealer = null;

    setTimeout(() => form.querySelector('input')?.focus(), 100);
  }

  function closeModal() {
    overlay.setAttribute('aria-hidden', 'true');
    overlay.classList.remove('raq-overlay--active');
    document.body.style.overflow = '';
    trigger.focus();
  }

  trigger.addEventListener('click', openModal);
  closeBtn.addEventListener('click', closeModal);
  $('raq-btn-close-success')?.addEventListener('click', closeModal);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) closeModal(); });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && overlay.classList.contains('raq-overlay--active')) closeModal();
  });

  // ─── 6. Dealer ZIP Lookup ─────────────────────────────────────────────────────
  findBtn?.addEventListener('click', async () => {
    const zipInput = $('raq-zip');
    const zip = zipInput?.value?.trim();

    clearError('err-zip');
    dealerCard.hidden = true;
    dealerNone.hidden = true;

    if (!zip || !/^\d{5}(-\d{4})?$/.test(zip)) {
      showError('err-zip', 'Please enter a valid 5-digit ZIP code.');
      zipInput?.focus();
      return;
    }

    setLoading(findBtn, true);

    try {
      // dealersZipLookup is defined in dealers-zip-lookup.js
      // It now returns dealer_id from the metaobject
      const dealer = await window.dealersZipLookup(zip);

      if (dealer) {
        quoteData.dealer = dealer;

        $('raq-dealer-name').textContent = dealer.name;

        const cityState = [dealer.city, dealer.state].filter(Boolean).join(', ');
        $('raq-dealer-meta').textContent = cityState
          ? `${cityState} · Serves ZIP ${dealer.zip}`
          : `Serves ZIP ${dealer.zip}`;

        $('raq-dealer-id').value    = dealer.dealer_id || '';
        $('raq-dealer-email').value = dealer.email     || '';
        dealerCard.hidden = false;

        // NEW — dealer found: show normal submit flow
        submitBtn.hidden = false;
        shadingDirectBtn.hidden = true;
      } else {
        dealerNone.hidden = false;

        // NEW — no dealer: block submission, offer Shading Direct instead
        quoteData.dealer = null;
        submitBtn.hidden = true;
        shadingDirectBtn.hidden = false;
      }
    } catch (err) {
      console.error('[RAQ] Dealer lookup failed:', err);
      showError('err-zip', 'Could not search dealers. Please try again.');
    } finally {
      setLoading(findBtn, false);
    }
  });

  $('raq-dealer-change')?.addEventListener('click', () => {
    dealerCard.hidden = true;
    dealerNone.hidden = true;         // NEW — clear no-dealer state too
    quoteData.dealer = null;
    submitBtn.hidden = true;          // NEW — re-lock until a new lookup resolves
    shadingDirectBtn.hidden = true;   // NEW
    $('raq-zip').value = '';
    $('raq-zip').focus();
  });

  // ─── 7. Form Submission → Zapier ─────────────────────────────────────────────
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(submitBtn, true);

    const payload = buildZapierPayload();

    try {
      await fetch(CONFIG.zapierWebhookUrl, {
        method: 'POST',
        // Zapier catch hooks accept both JSON and form-encoded.
        // Using no-cors + text/plain avoids a preflight — Zapier handles it fine.
        mode:    'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(payload),
      });

      // no-cors means we can't read the response — assume success if no throw
      form.style.display = 'none';
      successEl.hidden   = false;

      const msg = $('raq-success-msg');
      if (msg) {
        msg.textContent = quoteData.dealer
          ? `We've sent your configuration to our team and ${quoteData.dealer.name}. Expect to hear back within 1–2 business days.`
          : `We've sent your configuration to our team. Expect to hear back within 1–2 business days.`;
      }
    } catch (err) {
      console.error('[RAQ] Zapier webhook failed:', err);
      alert('Something went wrong submitting your request. Please try again or contact us directly.');
    } finally {
      setLoading(submitBtn, false);
    }
    
  });

  // ─── Validation ───────────────────────────────────────────────────────────────
  function validateForm() {
    let valid = true;

    if (!$('raq-first-name').value.trim()) {
      showError('err-first-name', 'First name is required.'); valid = false;
    }
    if (!$('raq-last-name').value.trim()) {
      showError('err-last-name', 'Last name is required.'); valid = false;
    }
    const email = $('raq-email').value.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      showError('err-email', 'A valid email address is required.'); valid = false;
    }
    const zip = $('raq-zip').value.trim();
    if (!zip || !/^\d{5}(-\d{4})?$/.test(zip)) {
      showError('err-zip', 'A valid ZIP code is required.'); valid = false;
    }

    // NEW — hard guard: no confirmed dealer, no submission, regardless of
    // button visibility state (belt-and-suspenders vs. the hidden attribute).
    if (!quoteData.dealer) {
      showError('err-zip', 'Please find your nearest dealer before submitting.');
      valid = false;
    }

    return valid;
  }

  // ─── Helpers ──────────────────────────────────────────────────────────────────
  function showError(id, msg) {
    const el = $(id);
    if (el) { el.textContent = msg; el.style.display = 'block'; }
  }
  function clearError(id) {
    const el = $(id);
    if (el) { el.textContent = ''; el.style.display = 'none'; }
  }
  function setLoading(btn, loading) {
    btn.classList.toggle('is-loading', loading);
    btn.disabled = loading;
  }
  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  document.querySelectorAll('.raq-form input, .raq-form textarea').forEach((el) => {
    el.addEventListener('input', () => {
      clearError('err-' + el.id.replace('raq-', ''));
    });
  });

})();